"use client";

import { Check } from "lucide-react";
import type { StepState, WaveSnapshot } from "./types";

function deriveLabelTone(state: StepState): string {
  if (state === "active") return "text-gray-900";
  if (state === "completed") return "text-gray-700";
  return "text-gray-400";
}

type BootStepProps = Readonly<{
  testId: string;
  label: string;
  snapshot: WaveSnapshot;
}>;

export function BootStep({ testId, label, snapshot }: BootStepProps) {
  const { state, readyCount, totalCount } = snapshot;
  const pct = totalCount === 0 ? 0 : (readyCount / totalCount) * 100;
  const labelTone = deriveLabelTone(state);
  const isPending = state === "pending";
  const isActive = state === "active";

  return (
    <li
      data-testid={testId}
      data-state={state}
      aria-current={isActive ? "step" : undefined}
      className="flex w-full flex-col gap-1.5"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2">
          <StepMarker state={state} />
          <span className={`text-sm font-medium ${labelTone}`}>{label}</span>
        </span>
        <span
          className={`text-[11px] tabular-nums ${
            isPending ? "text-gray-300" : "text-gray-500"
          }`}
          aria-hidden={isPending}
        >
          {isPending ? null : `${readyCount} / ${totalCount}`}
        </span>
      </div>
      {isPending ? null : (
        <div className="relative h-1 w-full overflow-hidden rounded-full bg-gray-100">
          {/* Native <progress> stays in the a11y tree but is visually
              hidden — the styled fill bar below carries the visuals,
              including the shimmer overlay during the active state. */}
          <progress
            value={readyCount}
            max={totalCount}
            aria-label={label}
            className="sr-only"
          />
          <div
            aria-hidden
            className="h-full rounded-full bg-emerald-600 transition-[width] duration-700 ease-out"
            style={{ width: `${pct}%` }}
          />
          {isActive && (
            <div
              aria-hidden
              className="animate-shimmer absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/60 to-transparent bg-[length:200%_100%]"
            />
          )}
        </div>
      )}
    </li>
  );
}

function StepMarker({ state }: Readonly<{ state: StepState }>) {
  if (state === "completed") {
    return (
      <span
        aria-hidden
        className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-600 text-white"
      >
        <Check className="h-3 w-3" strokeWidth={3} />
      </span>
    );
  }
  if (state === "active") {
    return (
      <span
        aria-hidden
        className="relative flex h-4 w-4 items-center justify-center"
      >
        <span className="absolute inset-0 rounded-full bg-emerald-600/20" />
        <span className="relative h-2 w-2 rounded-full bg-emerald-600" />
      </span>
    );
  }
  return (
    <span
      aria-hidden
      className="h-4 w-4 rounded-full border border-gray-300 bg-white"
    />
  );
}
